import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatSelectChange } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take, takeUntil, tap } from 'rxjs/operators';
import {
  AutoRegistrationService,
  BUSINESS_TYPE,
  CITY,
  ComponentBase,
  COUNTRY,
  ErrorFormComponent,
  KNOW_US,
  SavePhasesDataType,
  SharedService,
} from 'src/app/core';
import { BasicDetailsConfirmDialogComponent } from './basic-details-confirm-dialog.component';

@Component({
  selector: 'app-basic-details-step',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    ErrorFormComponent,
  ],
  templateUrl: './basic-details-step.component.html',
  styleUrl: './basic-details-step.component.scss',
})
export class BasicDetailsStepComponent extends ComponentBase implements OnInit {
  @Output() next = new EventEmitter<void>();

  form!: FormGroup;
  countries: COUNTRY[] = [];
  knowUsWays: KNOW_US[] = [];
  cities: CITY[] = [];
  businessType: BUSINESS_TYPE[] = [];
  businessTypeIds: number[] = [];
  notAllowedBusinessTypes: number[] = [];

  /** Skeleton: CountryId mat-form-field while getCountries is in progress */
  countriesLoading = false;
  /** Skeleton: City dropdown when getCities runs (on country change) */
  citiesLoading = false;
  /** Skeleton: Business Type section — only on initial load (loadBusinessTypes) */
  businessTypesLoading = false;

  dir = 'ltr';

  private get activeBusinessTypes(): BUSINESS_TYPE[] {
    return this.businessType.filter((type) => type.active);
  }

  constructor(
    private readonly sharedService: SharedService,
    private readonly autoRegistrationService: AutoRegistrationService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    readonly translateService: TranslateService
  ) {
    super();

    this.getCountriesList();
    this.getKnowUs();

    this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  get canSubmit(): boolean {
    return !!this.form && this.form.valid && !this.loading;
  }

  private initForm(): void {
    const regId = this.sharedService.registrationCycleLogId ?? null;
    this.form = new UntypedFormGroup({
      RegistrationCycleLogId: new UntypedFormControl(regId),
      BusinessName: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(30),
        Validators.minLength(4),
      ]),
      BusinessTypes: new UntypedFormControl([], Validators.required),
      CountryId: new UntypedFormControl(null, Validators.required),
      CityId: new UntypedFormControl(null, Validators.required),
      KnowUsWayId: new UntypedFormControl(null, [Validators.required]),
      IsCurrentlyUsingAnyOtherSystem: new UntypedFormControl(
        Validators.required
      ),
      SavePhasesDataType: new UntypedFormControl(SavePhasesDataType.Add),
    });
  }

  onChangeCountry(event: MatSelectChange): void {
    this.getCitiesForCountry(event.value);
  }

  onSelectBusinessType(busType: BUSINESS_TYPE): void {
    if (
      !busType.active &&
      this.notAllowedBusinessTypes.includes(busType.BusinessType)
    ) {
      return;
    }
    busType.active = !busType.active;
    this.updateNotAllowedBusinessTypes();
    this.form.patchValue({
      BusinessTypes: this.activeBusinessTypes.map((type) => type.Id),
    });
  }

  private updateNotAllowedBusinessTypes(): void {
    this.notAllowedBusinessTypes = Array.from(
      new Set(
        this.activeBusinessTypes
          .map((type) => type.NotAllowedWithBusinessTypes)
          .reduce((acc, val) => acc.concat(val), [])
      )
    );
  }

  private getCountriesList(): void {
    this.countriesLoading = true;
    this.sharedService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.countriesLoading = false;
        })
      )
      .subscribe((res) => {
        this.countries = res.Result ?? [];
        this.form?.patchValue({ CityId: null, BusinessTypes: [] });
        this.cities = [];
        this.businessType = [];
        // Load business types on initial load so they are always visible (no need to select country first)
        if (this.countries.length > 0) {
          this.loadBusinessTypes(this.countries[0].Id);
        }
      });
  }

  /** Load business types only (called once on initial load). */
  private loadBusinessTypes(countryId: number): void {
    if (!countryId) {
      this.businessType = [];
      return;
    }
    this.businessTypesLoading = true;
    this.sharedService
      .getBusinessType(countryId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.businessTypesLoading = false))
      )
      .subscribe((res) => {
        this.businessType = (res.Result ?? []).map((item) => ({
          ...item,
          active: false,
        }));
      });
  }

  private getKnowUs(): void {
    this.sharedService
      .getKnowUs()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.knowUsWays = res.Result;
      });
  }

  /** On country change: fetch cities only (no business-type skeleton). */
  private getCitiesForCountry(countryId: number): void {
    if (!countryId) {
      this.cities = [];
      return;
    }
    this.citiesLoading = true;
    this.sharedService
      .getCities(countryId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.citiesLoading = false))
      )
      .subscribe((res) => {
        this.cities = res.Result ?? [];
        this.form?.patchValue({ CityId: null });
      });
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (!this.canSubmit) {
      return;
    }

    const dialogRef = this.dialog.open(BasicDetailsConfirmDialogComponent, {
      width: '600px',
      autoFocus: false,
      restoreFocus: true,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (confirmed) this.onSubmit();
    });
  }

  private onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) {
        this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
        return;
      }
      this.loading = true;
      const payload = { ...this.form.value, RegistrationCycleLogId: regId };
      this.autoRegistrationService
        .saveBusinessType(payload)
        .pipe(
          take(1),
          tap(res => {
            if (this.sharedService.checkResponseStatus(res.Message, res.State)) {
              this.sharedService.playSound('assets/sounds/done.mp3');
              this.next.emit();
            }
          }),
          finalize(() => this.loading = false)
        ).subscribe();
    }
  }
}
